import { FormGroup, Validators } from "@angular/forms"
import { LayerType } from "./layer.model"
import { Subject } from "rxjs"
import { EffectsType } from "./ui-effects.model"
import { StringEncodings } from "../../services/image-draw.model"


export type ModifiableProperties =
  SharedProperties |
  OverlayProperties |
  BaseProperties |
  BuildingProperties |
  ColorOverlayProperties |
  ContourProperties |
  WaterProperties |
  OutlineProperties |
  BlurProperties |
  POIGlowProperties |
  MaterialOverlayProperties |
  TextureProperties |
  HeightGlowProperties |
  RoadProperties |
  SepiaProperties |
  DropShadowProperties |
  GridLinesProperties |
  NoiseProperties |
  ZoneProperties

export enum SharedProperties {
  Name = 'name',
  Opacity = 'opacity',
  BlendingMode = 'blend',
}

export enum OverlayProperties {
  BlurSize = 'blurSize',
  BlurSpread = 'blurSpread',
}

export enum ColorOverlayProperties {
  ColorOverlayColor = 'colorOverlayColor',
}

export enum WaterProperties {
  WaterColor = 'waterColor',
  WaterOpaqueDepth = 'waterOpaqueDepth',
  WaterMinOpacity = 'waterMinOpacity',
}

export enum GridLinesProperties {
  GridLinesColor = 'gridLinesColor',
  GridLinesOpacity = 'gridLinesOpacity',
  GridLinesDensity = 'gridLinesDensity',
  GridLinesStyle = 'gridLinesStyle',
  GridLinesOffsetX = 'gridLinesOffsetX',
  GridLinesOffsetY = 'gridLinesOffsetY',
}

export enum NoiseProperties {
  NoiseType = 'noiseType',
  NoiseFractileType = 'noiseFractileType',
  NoiseOpacity = 'noiseOpacity',
  NoiseSeed = 'noiseSeed',
  NoiseBlendingMode = 'noiseBlendingMode',
  NoiseHideUnderlyingLayer = 'noiseHideUnderlyingLayer',
  NoiseFrequency = 'noiseFrequency',
  NoiseFractileOctives = 'noiseFractileOctives',
  NoiseFractileGain = 'noiseFractileGain',
  NoiseFractileLacunarity = 'noiseFractileLacunarity',
  NoiseInvertMask = 'noiseInvertMask',
  NoiseColorGradient = 'noiseColorGradient',
}

export enum POIGlowProperties {
  POIGlowColor = 'poiGlowColor',
  POIGlowStrength = 'poiGlowStrength',
  POIDarknessColor = 'poiDarknessColor',
  POIGlowWidth = 'poiGlowWidth',
  POIGlowIncludedBuildings = 'poiGlowIncludedBuildings',
  POIGlowIncludedRoads = 'poiGlowIncludedRoads',
}

export enum ContourProperties {
  ContourTotalLines = 'contourTotalLines',
  ContourVisibleRange = 'contourShowLines',
  ContourResolution = 'contourResolution',
  ContourLineColor = 'contourLineColor',
  ContourLineWidth = 'contourLineWidth',
}

export enum HeightGlowProperties {
  HeightGlowRange = 'heightGlowRange',
  HeightGlowColor = 'heightGlowColor',
  HeightGlowRamp = 'heightGlowRamp',
}

export enum BaseProperties {
  NilPixelColor = 'baseBackgroundColor',
  NilPixelOpacity = 'baseBackgroundOpacity'
}

export enum BuildingProperties {
  BuildingColor = 'buildingColor',
  BuildingColorOpacity = 'buildingColorOpacity',
  BuildingGroup = 'buildingGroup',
  BuildingWeatheredRoofs = 'buildingWeatheredRoofs',
  BuildingWeatheredVariation = 'BuildingWeatheredVariation',
}

export enum ZoneProperties {
  ZoneGroup = 'zoneGroup',
  ZoneDetail = 'zoneDetail',
  ZoneOffset = 'zoneOffset',
  ZoneFillEnabled = 'zoneFillEnabled',
  ZoneFillColor = 'zoneFillColor',
  ZoneFillOpacity = 'zoneFillOpacity',
  ZoneStrokeEnabled = 'zoneStrokeEnabled',
  ZoneStrokeWidth = 'zoneStrokeWidth',
  ZoneStrokeColor = 'zoneStrokeColor',
  ZoneStrokeOpacity = 'zoneStrokeOpacity',
  ZoneStokeWidth = 'zoneStrokeWidth',
  ZoneStokeDashed = 'zoneStrokeDashed',
  ZoneStokeDashedLength = 'zoneStrokeDashedLength',
}

export enum RoadProperties {
  RoadColor = 'roadColor',
  RoadColorOpacity = 'roadColorOpacity',
  RoadGroup = 'roadGroup',
}

export enum MaterialOverlayProperties {
  MaterialOverlayColor = 'materialOverlayColor',
  MaterialOverlayOpacity = 'materialOverlayColorOpacity',
  MaterialOverlaySelector = 'materialOverlaySelector',
}

export enum OutlineProperties {
  OutlineInnerOuter = 'OutlineInnerOutline',
  OutlineWidth = 'OutlineWidth',
  OutlineColor = 'OutlineColor',
  OutlineOpacity = 'OutlineOpacity',
  OutlineSoftEdge = 'OutlineSoftEdge',
}

export enum BlurProperties {
  BlurStrength = 'BlurStrength',
}

export enum SepiaProperties {
  SepiaStrength = 'SepiaStrength',
}

export enum DropShadowProperties {
  DropShadowOffsetX = 'DropShadowOffsetX',
  DropShadowOffsetY = 'DropShadowOffsetY',
  DropShadowLength = 'DropShadowLength',
  DropShadowColor = 'DropShadowColor',
}

export enum TextureProperties {
  TextureSelector = 'textureSelector',
  TextureOpacity = 'textureOpactiy',
  TextureColor = 'textureColor',
  TextureScale = 'textureScale',
  TextureHideUnderlyingLayer = 'textureHideUnderlyingLayer',
}

export enum FormType {
  Text = 'text',
  InputNumber = 'input-number',
  SliderNumber = 'slider-number',
  Container = 'container',
  Color = 'color',
  Select = 'select',
  RangeSlider = 'range-slider',
  Multiselect = 'multiselect',
  Checkbox = 'checkbox',
  Gradient = 'gradient'
}

export interface Properties {
  propertyType: LayerType | EffectsType
  label: string
  forms: Form[]
  info?: string
  changeHandler: (fg: FormGroup, layerSelectionChange: Subject<void>) => void
  onInit?: (data: PropertiesInputData) => void
}

export interface PropertiesInputData{
  stringEncodings: StringEncodings
}

export interface Option {
  label: string
  value: any
}

export enum MoveLayerDirection {
  Up = 'up',
  Down = 'down'
}

export interface Form {
  id: ModifiableProperties | "container"
  label: string
  type: FormType
  defaultValue?: any
  placeholder?: string
  validators?: Validators
  suffix?: string
  allowDecimal?: boolean
  options?: Option[]
  hint?: string
  sliderOptions?: {
    min: number
    max: number
    step: number
  }
  children?: Form[]
}
